/**
 * Copyright 2025 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { enUs, type RegisteredLocale } from "@invertase/firebaseui-translations";
import type { FirebaseApp } from "firebase/app";
import { type Auth, getAuth, getRedirectResult, type MultiFactorResolver } from "firebase/auth";
import { deepMap, type DeepMapStore, map } from "nanostores";
import { type Behavior, type Behaviors, defaultBehaviors } from "./behaviors";
import type { InitBehavior, RedirectBehavior } from "./behaviors/utils";
import { type FirebaseUIState } from "./state";
import { handleFirebaseError } from "./errors";

export type FirebaseUIOptions = {
  app: FirebaseApp;
  auth?: Auth;
  locale?: RegisteredLocale;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  behaviors?: Behavior<any>[];
};

export type FirebaseUI = {
  app: FirebaseApp;
  auth: Auth;
  setLocale: (locale: RegisteredLocale) => void;
  state: FirebaseUIState;
  setState: (state: FirebaseUIState) => void;
  locale: RegisteredLocale;
  behaviors: Behaviors;
  multiFactorResolver?: MultiFactorResolver;
  setMultiFactorResolver: (multiFactorResolver?: MultiFactorResolver) => void;
  redirectError?: Error;
  setRedirectError: (error?: Error) => void;
};

export const $config = map<Record<string, DeepMapStore<FirebaseUI>>>({});

export type FirebaseUIStore = DeepMapStore<FirebaseUI>;

export function initializeUI(config: FirebaseUIOptions, name: string = "[DEFAULT]"): FirebaseUIStore {
  // Reduce the behaviors to a single object.
  const behaviors = config.behaviors?.reduce<Behavior>((acc, behavior) => {
    return {
      ...acc,
      ...behavior,
    };
  }, defaultBehaviors as Behavior);

  $config.setKey(
    name,
    deepMap<FirebaseUI>({
      app: config.app,
      auth: config.auth || getAuth(config.app),
      locale: config.locale ?? enUs,
      setLocale: (locale: RegisteredLocale) => {
        const current = $config.get()[name]!;
        current.setKey(`locale`, locale);
      },
      state: "idle",
      setState: (state: FirebaseUIState) => {
        const current = $config.get()[name]!;
        current.setKey(`state`, state);
      },
      // Since we've got config.behaviors?.reduce above, we need to default to defaultBehaviors
      // if no behaviors are provided, as they wont be in the reducer.
      behaviors: behaviors ?? (defaultBehaviors as Behavior),
      multiFactorResolver: undefined,
      setMultiFactorResolver: (resolver?: MultiFactorResolver) => {
        const current = $config.get()[name]!;
        current.setKey(`multiFactorResolver`, resolver);
      },
      redirectError: undefined,
      setRedirectError: (error?: Error) => {
        const current = $config.get()[name]!;
        current.setKey(`redirectError`, error);
      },
    })
  );

  const store = $config.get()[name]!;
  const ui = store.get();

  // If we're client-side, execute the init and redirect behaviors.
  if (typeof window !== "undefined") {
    const initBehaviors: InitBehavior[] = [];
    const redirectBehaviors: RedirectBehavior[] = [];

    for (const behavior of Object.values(ui.behaviors)) {
      if (behavior.type === "redirect") {
        redirectBehaviors.push(behavior);
      } else if (behavior.type === "init") {
        initBehaviors.push(behavior);
      }
    }

    if (initBehaviors.length > 0) {
      store.setKey("state", "loading");
      ui.auth.authStateReady().then(() => {
        Promise.all(initBehaviors.map((behavior) => behavior.handler(ui))).then(() => {
          store.setKey("state", "idle");
        });
      });
    }

    getRedirectResult(ui.auth)
      .then((result) => {
        return Promise.all(redirectBehaviors.map((behavior) => behavior.handler(ui, result)));
      })
      .catch((error) => {
        try {
          handleFirebaseError(ui, error);
        } catch (error) {
          ui.setRedirectError(error instanceof Error ? error : new Error(String(error)));
        }
      });
  }

  return store;
}
